import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { migrateCampaignIds } from '../scripts/migrateCampaignIds';

const MigrationPage: React.FC = () => {
  const navigate = useNavigate();
  const [status, setStatus] = useState<string>('Ready to migrate campaigns');
  const [isRunning, setIsRunning] = useState(false);
  const [logs, setLogs] = useState<string[]>([]);

  const addLog = (message: string) => {
    setLogs(prev => [...prev, message]);
    console.log(message);
  };

  const handleMigration = async () => {
    setIsRunning(true);
    setStatus('🔄 Migration in progress...');
    setLogs([]);
    
    try {
      addLog('🔄 Starting campaign ID migration...');
      addLog('========================================');
      
      await migrateCampaignIds();
      
      addLog('========================================');
      addLog('🎉 Migration completed successfully!');
      setStatus('✅ Migration completed successfully! Check logs below for details.');
    } catch (error) {
      const errorMessage = `❌ Migration failed: ${(error as Error).message}`;
      addLog(errorMessage);
      setStatus(errorMessage);
      console.error(error);
    } finally {
      setIsRunning(false);
    }
  };

  return (
    <div style={styles.container}>
      <div style={styles.card}>
        <div style={styles.header}>
          <button onClick={() => navigate('/')} style={styles.backButton}>
            ← Back
          </button>
          <h1 style={styles.title}>🔄 Campaign ID Migration</h1>
        </div>
        
        <div style={styles.infoBox}>
          <p style={styles.infoTitle}><strong>What this does:</strong></p>
          <ul style={styles.infoList}>
            <li>Adds <code style={styles.code}>campaignId</code> field to all campaigns without one</li>
            <li>Generates unique IDs in format: <code style={styles.code}>Wiin_Title_Timestamp_Random</code></li>
            <li>Skips campaigns that already have a campaignId</li>
            <li>Safe to run multiple times</li>
          </ul>
        </div>

        <div style={styles.statusBox}>
          <p style={styles.status}>{status}</p>
        </div>

        <button
          onClick={handleMigration}
          disabled={isRunning}
          style={{
            ...styles.button,
            ...(isRunning ? styles.buttonDisabled : {}),
          }}
        >
          {isRunning ? '⏳ Migrating...' : '🚀 Run Migration'}
        </button>

        {logs.length > 0 && (
          <div style={styles.logsBox}>
            <h3 style={styles.logsTitle}>📋 Migration Logs:</h3>
            <div style={styles.logsContent}>
              {logs.map((log, index) => (
                <div key={index} style={styles.logLine}>{log}</div>
              ))}
            </div>
          </div>
        )}

        <div style={styles.warning}>
          <strong>⚠️ Important:</strong> After migration, you need to:
          <ol style={styles.warningList}>
            <li>Go to Firebase Console → Firestore → campaigns</li>
            <li>Copy the <code style={styles.code}>campaignId</code> value for each campaign</li>
            <li>Update your Playable webhook configurations with these new values</li>
            <li>Deploy the updated Cloud Function: <code style={styles.code}>firebase deploy --only functions</code></li>
            <li>Deploy Firestore indexes: <code style={styles.code}>firebase deploy --only firestore:indexes</code></li>
          </ol>
        </div>

        <div style={styles.helpBox}>
          <p><strong>📚 Need help?</strong></p>
          <p>See <code style={styles.code}>CAMPAIGN-ID-MIGRATION-GUIDE.md</code> for detailed instructions.</p>
        </div>
      </div>
    </div>
  );
};

const styles: { [key: string]: React.CSSProperties } = {
  container: {
    minHeight: '100vh',
    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    padding: '20px',
  },
  card: {
    background: 'white',
    borderRadius: '12px',
    padding: '40px',
    maxWidth: '800px',
    width: '100%',
    boxShadow: '0 10px 40px rgba(0,0,0,0.2)',
  },
  header: {
    marginBottom: '20px',
  },
  backButton: {
    background: 'transparent',
    border: '1px solid #ddd',
    borderRadius: '6px',
    padding: '8px 16px',
    cursor: 'pointer',
    fontSize: '14px',
    marginBottom: '15px',
    transition: 'all 0.2s',
  },
  title: {
    fontSize: '28px',
    fontWeight: 'bold',
    margin: 0,
    color: '#333',
  },
  infoBox: {
    background: '#f0f4ff',
    border: '1px solid #d0d7ff',
    borderRadius: '8px',
    padding: '20px',
    marginBottom: '20px',
  },
  infoTitle: {
    margin: '0 0 10px 0',
    fontSize: '16px',
  },
  infoList: {
    margin: '10px 0 0 0',
    paddingLeft: '20px',
  },
  code: {
    background: '#e9ecef',
    padding: '2px 6px',
    borderRadius: '4px',
    fontSize: '13px',
    fontFamily: 'monospace',
  },
  statusBox: {
    background: '#f9f9f9',
    border: '1px solid #ddd',
    borderRadius: '8px',
    padding: '20px',
    marginBottom: '20px',
    minHeight: '50px',
    display: 'flex',
    alignItems: 'center',
  },
  status: {
    margin: 0,
    fontSize: '16px',
    color: '#555',
    fontWeight: '500',
  },
  button: {
    width: '100%',
    padding: '15px',
    fontSize: '18px',
    fontWeight: 'bold',
    color: 'white',
    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    border: 'none',
    borderRadius: '8px',
    cursor: 'pointer',
    transition: 'transform 0.2s, opacity 0.2s',
    marginBottom: '20px',
  },
  buttonDisabled: {
    opacity: 0.6,
    cursor: 'not-allowed',
  },
  logsBox: {
    background: '#1e1e1e',
    borderRadius: '8px',
    padding: '20px',
    marginBottom: '20px',
    maxHeight: '300px',
    overflow: 'auto',
  },
  logsTitle: {
    margin: '0 0 15px 0',
    color: '#fff',
    fontSize: '16px',
  },
  logsContent: {
    fontFamily: 'monospace',
    fontSize: '13px',
  },
  logLine: {
    color: '#00ff00',
    marginBottom: '5px',
    lineHeight: '1.5',
  },
  warning: {
    padding: '20px',
    background: '#fff3cd',
    border: '1px solid #ffc107',
    borderRadius: '8px',
    fontSize: '14px',
    color: '#856404',
    marginBottom: '20px',
  },
  warningList: {
    margin: '10px 0 0 0',
    paddingLeft: '20px',
  },
  helpBox: {
    padding: '15px',
    background: '#e7f3ff',
    border: '1px solid #b3d9ff',
    borderRadius: '8px',
    fontSize: '14px',
    color: '#004085',
  },
};

export default MigrationPage;
